/**
 * The icon catalogue for user-defined categories.
 *
 * A category stores only its icon's Phosphor name ("ForkKnife"); this module is
 * the single place that turns that string back into something renderable. The
 * imports are explicit rather than a `import * as Phosphor` barrel so the
 * bundler ships the ~100 icons below instead of all 1,500 in the package.
 *
 * `CategoryIcon` falls back to a generic tag for any name it cannot resolve, so
 * a category whose icon was removed from this list — or written directly into
 * the database — still renders instead of crashing the tree.
 */
import {
  // Money & work
  Money, CurrencyEur, Wallet, PiggyBank, Coins, CreditCard, Bank, Briefcase,
  Buildings, Storefront, Receipt, ChartLineUp, ChartPie, TrendUp, TrendDown,
  Handshake,
  // Home & bills
  House, Lightning, Drop, Fire, WifiHigh, Phone, Wrench, Hammer, Toolbox,
  PaintBrush, Plug, Couch, Bed, Key,
  // Food & drink
  ForkKnife, Coffee, Pizza, Hamburger, BeerStein, Wine, Martini, Champagne,
  Basket, ShoppingCart, ShoppingBag, Carrot, Cake, IceCream, Egg, Fish,
  // Getting around
  Car, Bus, Train, Airplane, Bicycle, Motorcycle, Taxi, Boat, GasPump, MapPin,
  Compass, Globe, SuitcaseRolling, Tent, Mountains,
  // Health & sport
  Heartbeat, Pill, FirstAid, Tooth, Stethoscope, Syringe, Bandaids, Barbell,
  PersonSimpleRun, SoccerBall, Basketball, Baseball, Trophy, Wheelchair,
  // Life & leisure
  FilmSlate, MusicNotes, GameController, Camera, BookOpen, GraduationCap,
  Backpack, Gift, Confetti, Balloon, Ticket, Television, Headphones, Guitar,
  Palette, Popcorn, Church, Cigarette,
  // People & pets
  Users, UserCircle, Baby, HandHeart, Heart, Dog, Cat, PawPrint, Bird,
  Butterfly, Cow,
  // Things & tech
  DeviceMobile, Laptop, Desktop, Cloud, Robot, Watch, Bag, TShirt, Sneaker,
  Scissors, Package, Truck, Newspaper, Note, Bookmark,
  // Nature & misc
  Sun, Umbrella, Tree, Leaf, Plant, Flower, Waves, Star, Repeat, Tag,
} from "@phosphor-icons/react";

export const DEFAULT_ICON = "Tag";

/** Icons offered in the picker, grouped so a long list stays scannable. */
export const ICON_GROUPS = [
  {
    label: "Money & work",
    icons: { Money, CurrencyEur, Wallet, PiggyBank, Coins, CreditCard, Bank,
      Briefcase, Buildings, Storefront, Receipt, ChartLineUp, ChartPie, TrendUp,
      TrendDown, Handshake },
  },
  {
    label: "Home & bills",
    icons: { House, Lightning, Drop, Fire, WifiHigh, Phone, Wrench, Hammer,
      Toolbox, PaintBrush, Plug, Couch, Bed, Key },
  },
  {
    label: "Food & drink",
    icons: { ForkKnife, Coffee, Pizza, Hamburger, BeerStein, Wine, Martini,
      Champagne, Basket, ShoppingCart, ShoppingBag, Carrot, Cake, IceCream,
      Egg, Fish },
  },
  {
    label: "Getting around",
    icons: { Car, Bus, Train, Airplane, Bicycle, Motorcycle, Taxi, Boat, GasPump,
      MapPin, Compass, Globe, SuitcaseRolling, Tent, Mountains },
  },
  {
    label: "Health & sport",
    icons: { Heartbeat, Pill, FirstAid, Tooth, Stethoscope, Syringe, Bandaids,
      Barbell, PersonSimpleRun, SoccerBall, Basketball, Baseball, Trophy,
      Wheelchair },
  },
  {
    label: "Life & leisure",
    icons: { FilmSlate, MusicNotes, GameController, Camera, BookOpen,
      GraduationCap, Backpack, Gift, Confetti, Balloon, Ticket, Television,
      Headphones, Guitar, Palette, Popcorn, Church, Cigarette },
  },
  {
    label: "People & pets",
    icons: { Users, UserCircle, Baby, HandHeart, Heart, Dog, Cat, PawPrint,
      Bird, Butterfly, Cow },
  },
  {
    label: "Things & tech",
    icons: { DeviceMobile, Laptop, Desktop, Cloud, Robot, Watch, Bag, TShirt,
      Sneaker, Scissors, Package, Truck, Newspaper, Note, Bookmark },
  },
  {
    label: "Nature & other",
    icons: { Sun, Umbrella, Tree, Leaf, Plant, Flower, Waves, Star, Repeat, Tag },
  },
];

/** Flat name -> component lookup, built from the groups so the two can't drift. */
export const ICONS = ICON_GROUPS.reduce(
  (all, group) => Object.assign(all, group.icons),
  {},
);

/** Colours offered in the picker — the chart palette, so the donut stays legible. */
export const CATEGORY_PALETTE = [
  "#00FFA3", "#3388FF", "#FF3366", "#F5C542", "#B27BFF", "#00D4D4",
  "#FF8A3D", "#7CFF6B", "#FF5FA2", "#5C9DFF", "#E0E0E0", "#A1A1AA",
];

export const DEFAULT_COLOR = "#A1A1AA";

export function getIcon(name) {
  return ICONS[name] || ICONS[DEFAULT_ICON];
}

/**
 * Render a category's icon. Accepts either a category object or a bare icon
 * name, because callers have one or the other depending on where they sit.
 */
export function CategoryIcon({ category, name, size = 16, weight = "bold", ...rest }) {
  const Icon = getIcon(name ?? category?.icon);
  const color = rest.color ?? category?.color;
  return <Icon size={size} weight={weight} {...rest} color={color} />;
}

/**
 * Index a `{ income, expense }` response so a transaction can find the category
 * it belongs to. Keyed on both kind and name because the two kinds are separate
 * namespaces — an income "Investments" is not the expense one.
 */
export function categoryIndex(categories) {
  const index = {};
  for (const kind of ["income", "expense"]) {
    for (const c of categories?.[kind] || []) index[`${kind}:${c.name}`] = c;
  }
  return index;
}

export const lookupCategory = (index, kind, name) => index?.[`${kind}:${name}`];

/**
 * Name -> colour map for a list of categories, so charts can colour a slice by
 * the category the user chose rather than by its position in the data.
 */
export function colorMap(categories) {
  const map = {};
  for (const c of categories || []) map[c.name] = c.color || DEFAULT_COLOR;
  return map;
}
